import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import Filter from './Filter'
import Pagination from './Pagination'

import { Flex } from 'reflexbox/styled-components'

import { DefaultContainer } from 'components/Container'

import { TAG_ALL_ARTICLES } from 'consts'

const cardPadding = 32

// TODO: keep the same card height when pagination occurs. check out this loom video,
// the bottom asset moves on pagination because the card heights change:
// https://www.loom.com/share/bce8921d4f924f10bba05257b15b0651

const Container = styled(Flex).withConfig({
  shouldForwardProp: (prop) => ['postsCentered'].indexOf(prop) === -1,
})`
  flex-wrap: wrap;

  ${({ postsCentered }) =>
    postsCentered &&
    `
    justify-content: center;
  `}
`

const twoColumnLeftPadding = (i) => (i % 2 === 0 ? 0 : cardPadding / 2)
const twoColumnRightPadding = (i) => (i % 2 === 0 ? cardPadding / 2 : 0)

const threeColumnLeftPadding = (i) =>
  i % 3 === 0 ? 0 : i % 3 === 1 ? cardPadding / 3 : (cardPadding * 2) / 3
const threeColumnRightPadding = (i) =>
  i % 3 === 0 ? (cardPadding * 2) / 3 : i % 3 === 1 ? cardPadding / 3 : 0

const fourColumnLeftPadding = (i) => {
  switch (i % 4) {
    case 0:
      return 0
    case 1:
      return (cardPadding * 1) / 4
    case 2:
      return cardPadding / 2
    case 3:
      return (cardPadding * 3) / 4
  }
}
const fourColumnRightPadding = (i) => {
  switch (i % 4) {
    case 0:
      return (cardPadding * 3) / 4
    case 1:
      return cardPadding / 2
    case 2:
      return (cardPadding * 1) / 4
    case 3:
      return 0
  }
}

const getNumericalValueForBreakpoint = (breakpoint) => {
  switch (breakpoint) {
    case 'mobile':
      return 0
    case 'tablet':
      return 1
    case 'desktop':
      return 2
    case 'giant':
      return 3
    case 'beyondGiant':
      return 4
    case null:
    case undefined:
      return 0
    default:
      console.warn('Unknown breakpoint, not applying single column layout')
      return 0
  }
}

const renderPagination = (
  amount,
  pageNumber,
  pagination,
  baseSlug,
  tag,
  currentEnd,
  scrollName,
  posts,
  handleChangeDisplayedPosts
) => {
  return (
    <Pagination
      amountPosts={amount}
      baseSlug={baseSlug}
      tag={tag}
      pageNumber={pageNumber}
      pageAmount={pagination.pageAmount}
      currentEnd={currentEnd}
      scrollName={scrollName}
      isStateful={pagination.isStateful}
      handleChangeDisplayedPosts={(pageNumber) =>
        handleChangeDisplayedPosts(pageNumber, pagination.pageAmount, posts)
      }
    />
  )
}

const PostGrid = ({
  allPosts,
  postComponent,
  postProps,
  tag,
  baseSlug,
  pagination,
  filterValues,
  columns,
  singleColumnBreakpoint,
  referenceLink,
  scrollName,
  amountOfPosts,
  navBarHeight,
  postsCentered,
  ...others
}) => {
  const [posts, setPosts] = useState([])
  const [displayedPosts, setDisplayedPosts] = useState([])
  const [end, setEnd] = useState(0)
  const [pageNumber, setPageNumber] = useState(0)

  // TODO: dont let any arbitrary postComponent be passed in, only allow
  // components that are in the summaryCards folder as PostComponent.
  // the prop passed in be a string, and we should enumerate the values in PropTypes
  const PostComponent = postComponent
  let currentEnd = end

  useEffect(() => {
    let filteredPosts = allPosts

    if (filterValues && tag !== TAG_ALL_ARTICLES) {
      filteredPosts = filteredPosts.filter(
        (post) =>
          // case insensitive
          post?.tags?.includes((tag || '').toLowerCase()) ||
          post?.tags?.includes(tag || '')
      )
    }

    if (amountOfPosts) {
      filteredPosts = filteredPosts?.slice(0, amountOfPosts)
    }

    setDisplayedPosts(filteredPosts)
    setPosts(filteredPosts)

    if (pagination) {
      handleChangeDisplayedPosts(
        pagination.currentPageNumber,
        pagination.pageAmount,
        filteredPosts
      )
    }
  }, [allPosts])

  const handleChangeDisplayedPosts = (page, pageAmount, posts) => {
    const pagedPosts = []

    currentEnd = Math.min(page * pageAmount + pageAmount, posts.length)

    for (let i = page * pageAmount; i < currentEnd; i++) {
      pagedPosts.push(posts[i])
    }

    setEnd(currentEnd)
    setPageNumber(page)
    setDisplayedPosts(pagedPosts)
  }

  const renderFilters = () => {
    return (
      <Filter
        values={filterValues}
        baseSlug={baseSlug}
        selectedValue={tag}
        referenceLink={referenceLink}
        scrollName={scrollName}
        navBarHeight={navBarHeight}
      />
    )
  }

  return (
    <DefaultContainer
      maxWidth={3000}
      style={{ width: '100%' }}
      id="summary-grid"
      {...others}
    >
      {filterValues && renderFilters()}
      <Container postsCentered={postsCentered} data-cy="summary-grid-parent">
        {displayedPosts.map((post, i) => {
          return (
            <Flex
              key={i}
              width={[
                1,
                1,
                getNumericalValueForBreakpoint(singleColumnBreakpoint) >= 2
                  ? 1
                  : 1 / 2,
                getNumericalValueForBreakpoint(singleColumnBreakpoint) >= 3
                  ? 1
                  : columns >= 3
                    ? 1 / 3
                    : 1 / 2,
                getNumericalValueForBreakpoint(singleColumnBreakpoint) >= 4
                  ? 1
                  : columns >= 4
                    ? 1 / 4
                    : columns === 3
                      ? 1 / 3
                      : 1 / 2,
              ]}
              pl={[
                0,
                0,
                getNumericalValueForBreakpoint(singleColumnBreakpoint) >= 2
                  ? 0
                  : twoColumnLeftPadding(i),
                getNumericalValueForBreakpoint(singleColumnBreakpoint) >= 3
                  ? 0
                  : columns >= 3
                    ? threeColumnLeftPadding(i)
                    : twoColumnLeftPadding(i),
                getNumericalValueForBreakpoint(singleColumnBreakpoint) >= 4
                  ? 0
                  : columns >= 4
                    ? fourColumnLeftPadding(i)
                    : columns === 3
                      ? threeColumnLeftPadding(i)
                      : twoColumnLeftPadding(i),
              ]}
              pr={[
                0,
                0,
                getNumericalValueForBreakpoint(singleColumnBreakpoint) >= 2
                  ? 0
                  : twoColumnRightPadding(i),
                getNumericalValueForBreakpoint(singleColumnBreakpoint) >= 3
                  ? 0
                  : columns >= 3
                    ? threeColumnRightPadding(i)
                    : twoColumnRightPadding(i),
                getNumericalValueForBreakpoint(singleColumnBreakpoint) >= 4
                  ? 0
                  : columns >= 4
                    ? fourColumnRightPadding(i)
                    : columns === 3
                      ? threeColumnRightPadding(i)
                      : twoColumnRightPadding(i),
              ]}
              py={[cardPadding / 2]}
              flexDirection="column"
            >
              <PostComponent
                {...postProps}
                {...post}
                key={i}
                basePath={`/${baseSlug}`}
                showDate={!post?.tags?.includes('customer stories')}
                selectedTag={tag}
              />
            </Flex>
          )
        })}
      </Container>
      {pagination &&
        renderPagination(
          posts.length,
          pageNumber,
          pagination,
          baseSlug,
          tag,
          end,
          scrollName,
          posts,
          handleChangeDisplayedPosts
        )}
    </DefaultContainer>
  )
}

PostGrid.defaultProps = {
  postProps: {},
  columns: 3,
}

PostGrid.propTypes = {
  allPosts: PropTypes.array.isRequired,
  postProps: PropTypes.object,
  tag: PropTypes.string,
  baseSlug: PropTypes.string.isRequired,
  pagination: PropTypes.shape({
    pageAmount: PropTypes.number,
    currentPageNumber: PropTypes.number,
    currentPageEnd: PropTypes.number,
    totalPosts: PropTypes.number,
  }),
  postComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object])
    .isRequired,
  columns: PropTypes.oneOf([2, 3, 4]),
  filterValues: PropTypes.array,
  referenceLink: PropTypes.shape({
    to: PropTypes.string.isRequired,
    mobileText: PropTypes.string.isRequired,
    desktopText: PropTypes.string.isRequired,
  }),
  amountOfPosts: PropTypes.number,
  scrollName: PropTypes.string,
  navBarHeight: PropTypes.number,
  // centers posts via flex, might wanna remove and make the standard...
  postsCentered: PropTypes.bool,
  singleColumnBreakpoint: PropTypes.string,
}

export default PostGrid
